import { useEffect, useRef } from 'react';
import { 
  exraUrl,
  deepLinkView,
  deepLinkOrigin,
  deepLinkExtraParam,
  deepLinkTarget,
}                    from 'utils/constants'
import { 
  objType, 
  updateInfoType,
  userInfoType
}                    from 'types';
import { store }     from 'index';

const useGoToVideoPage = (
  dataUpdateState:    updateInfoType,
  userInfo:           userInfoType,
  selectedApp:        objType,
  storageUpdateState: boolean,
  goToVideoOnce:      boolean,
  setGoToVideoOnce:   (val: boolean) => void,
) => {
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef(0);
  const attemptedRef = useRef(false);
  const MAX_RETRIES = 15; // Try for up to ~7.5 seconds (15 * 500ms)
  const RETRY_DELAY = 500; // 500ms between retries

  // Function to check if content is ready
  const isContentReady = () => {
    if (!deepLinkTarget) return false;

    // Check if we need teacher content for this deep link
    const needsTeacherContent = deepLinkOrigin === 'teacherLessons' || 
                                 deepLinkOrigin === 'teacher' || 
                                 deepLinkOrigin === 'teachers' ||
                                 deepLinkOrigin === 'mivdakim';

    // Check if basic content is ready
    const contentReady = dataUpdateState.content || !!storageUpdateState;
    
    // If we don't need teacher content, just check basic content
    if (!needsTeacherContent) {
      return contentReady;
    }

    // For teacher content, check both flags and arrays
    const teacherContentReady = dataUpdateState.teacherContent;
    
    // Also check if teacher arrays are actually populated
    const state = store.getState();
    const teacherArraysPopulated = 
      (state.content.bigTeacherArr?.length > 0 || 
       state.content.teacherLessons?.length > 0 || 
       state.content.mivdakim?.length > 0);

    return contentReady && teacherContentReady && teacherArraysPopulated;
  };

  // Function to attempt navigation
  const attemptNavigation = async (): Promise<boolean> => {
    if (!deepLinkTarget || goToVideoOnce) return false;

    const ready = isContentReady();
    if (!ready) return false;

    try {
      const func = await import("functions/actions/go_to_video_page");
      const success = func.goToVideoPage(
        deepLinkTarget, 
        exraUrl, 
        setGoToVideoOnce, 
        {
          view:   deepLinkView,
          origin: deepLinkOrigin,
          extraId: deepLinkExtraParam || exraUrl,
        }
      );
      
      if (success) {
        retryCountRef.current = 0;
        attemptedRef.current = true;
        return true;
      }
    } catch (error) {
      console.error('[DeepLink] Error during navigation:', error);
    }
    
    return false;
  };

  // Retry function
  const scheduleRetry = () => {
    // Don't schedule if already attempted or max retries reached
    if (attemptedRef.current || retryCountRef.current >= MAX_RETRIES) {
      if (retryCountRef.current >= MAX_RETRIES) {
        console.warn('[DeepLink] Max retries reached, content may not be loaded', {
          deepLinkTarget,
          deepLinkOrigin,
          retryCount: retryCountRef.current,
        });
        attemptedRef.current = true;
        setGoToVideoOnce(true);
        localStorage.removeItem('videoPage');
      }
      return;
    }

    retryCountRef.current++;
    retryTimeoutRef.current = setTimeout(async () => {
      // Check again if we should continue
      if (attemptedRef.current || goToVideoOnce || !deepLinkTarget) {
        return;
      }
      
      const success = await attemptNavigation();
      if (!success && retryCountRef.current < MAX_RETRIES) {
        scheduleRetry();
      }
    }, RETRY_DELAY);
  };

  // Track previous values to detect actual changes
  const prevContentReadyRef = useRef(false);
  const prevDeepLinkTargetRef = useRef<string | null>(null);

  // Main effect - tries immediately and then retries if needed
  useEffect(() => { 
    // If deep link target changed, reset everything
    if (prevDeepLinkTargetRef.current !== deepLinkTarget) {
      prevDeepLinkTargetRef.current = deepLinkTarget;
      attemptedRef.current = false;
      retryCountRef.current = 0;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    }

    if (!deepLinkTarget || goToVideoOnce || attemptedRef.current) {
      return;
    }

    const contentReady = isContentReady();
    
    // Only attempt if content readiness changed from false to true
    // or if this is the first attempt
    if (contentReady && (!prevContentReadyRef.current || retryCountRef.current === 0)) {
      prevContentReadyRef.current = true;
      
      // Try immediately
      attemptNavigation().then(success => {
        if (!success && retryCountRef.current === 0) {
          // If not successful on first try, schedule retry
          scheduleRetry();
        }
      });
    } else if (!contentReady) {
      prevContentReadyRef.current = false;
    }

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  },[dataUpdateState.content, dataUpdateState.teacherContent, storageUpdateState, goToVideoOnce, deepLinkTarget, deepLinkOrigin])

  useEffect(() => {
    if ( 
      !!deepLinkTarget.length && 
      userInfo.userLogedIn === 'false' && 
      (!localStorage.getItem('videoPage') || (!!localStorage.getItem('videoPage') || localStorage.getItem('videoPage') !== deepLinkTarget) )) {
      localStorage.setItem('videoPage', deepLinkTarget)
    }
  },[userInfo, deepLinkTarget])
}

export default useGoToVideoPage